import './util/handleError';
import './util/setupServiceWorker';
import './global/init';
import './util/accountSwitcher'; // Import account switcher for global functions

import React from './lib/teact/teact';
import TeactDOM from './lib/teact/teact-dom';
import {
  getActions, getGlobal,
} from './global';

import {
  DEBUG, MULTITAB_LOCALSTORAGE_KEY, STRICTERDOM_ENABLED,
} from './config';
import { enableStrict, requestMutation } from './lib/fasterdom/fasterdom';
import { selectTabState } from './global/selectors';
import { betterView } from './util/betterView';
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole';
import { initGlobal } from './util/init';
import { initLocalization } from './util/localization';
import { requestGlobal, subscribeToMultitabBroadcastChannel } from './util/multitab';
import { checkAndAssignPermanentWebVersion } from './util/permanentWebVersion';
import { onBeforeUnload } from './util/schedulers';
import updateWebmanifest from './util/updateWebmanifest';
import { IS_MULTITAB_SUPPORTED } from './util/windowEnvironment';
import { getAllScheduledMessages, deleteScheduledMessage, onScheduledMessageAdded, onScheduledMessageRemoved } from './util/scheduledMessagesManager';

import App from './components/App';

import './assets/fonts/roboto.css';
import './styles/index.scss';

if (STRICTERDOM_ENABLED) {
  enableStrict();
}

init();

async function init() {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT');
  }

  if (!(window as any).isCompatTestPassed) return;

  checkAndAssignPermanentWebVersion();

  await window.electron?.restoreLocalStorage();

  if (IS_MULTITAB_SUPPORTED) {
    subscribeToMultitabBroadcastChannel();
    await requestGlobal(APP_VERSION);
    localStorage.setItem(MULTITAB_LOCALSTORAGE_KEY, '1');
    onBeforeUnload(() => {
      const global = getGlobal();
      if (Object.keys(global.byTabId).length === 1) {
        localStorage.removeItem(MULTITAB_LOCALSTORAGE_KEY);
      }
    });
  }

  await initGlobal();
  getActions().init();

  getActions().updateShouldEnableDebugLog();
  getActions().updateShouldDebugExportedSenders();

  const global = getGlobal();

  initLocalization(global.settings.byKey.language, true);

  if (IS_MULTITAB_SUPPORTED) {
    subscribeToMasterChange((isMasterTab) => {
      getActions()
        .switchMultitabRole({ isMasterTab }, { forceSyncOnIOs: true });
    });
    const shouldReestablishMasterToSelf = getGlobal().authState !== 'authorizationStateReady';
    establishMultitabRole(shouldReestablishMasterToSelf);
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    updateWebmanifest();

    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );

    betterView();
  });

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(getGlobal()));
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', getGlobal());
    });
  }

  const activeTimeouts = new Map<string, NodeJS.Timeout>();

  function scheduleMessageTimeout(message: { id: string; chatId: string; text: string; scheduledAt: number; attachments?: any[] }) {
    const now = Date.now();
    const delay = message.scheduledAt - now;
    
    if (delay <= 0) {
      sendScheduledMessage(message);
      return;
    }

    if (activeTimeouts.has(message.id)) {
      clearTimeout(activeTimeouts.get(message.id));
    }

    const timeout = setTimeout(() => {
      sendScheduledMessage(message);
      activeTimeouts.delete(message.id);
    }, delay);

    activeTimeouts.set(message.id, timeout);
  }

  function sendScheduledMessage(message: { id: string; chatId: string; text: string; entities?: any; attachments?: any[] }) {
    const actions = getActions();
    
    let processedAttachments = message.attachments;
    if (processedAttachments && processedAttachments.length > 0) {
      processedAttachments = processedAttachments.map((attachment) => {
        if (attachment.blobUrl && attachment.blobUrl.startsWith('blob:')) {
          return attachment;
        }
        
        if (attachment.blobUrl && attachment.blobUrl.startsWith('data:')) {
          try {
            const blob = dataURLToBlob(attachment.blobUrl);
            const blobUrl = URL.createObjectURL(blob);
            
            let previewBlobUrl = attachment.previewBlobUrl;
            if (previewBlobUrl && previewBlobUrl.startsWith('data:')) {
              const previewBlob = dataURLToBlob(previewBlobUrl);
              previewBlobUrl = URL.createObjectURL(previewBlob);
            }
            
            return {
              ...attachment,
              blobUrl,
              previewBlobUrl,
            };
          } catch (error) {
            console.error('Error converting attachment back to blob URL:', error);
            return attachment;
          }
        }
        
        return attachment;
      });
    }
    
    actions.sendMessage({
      messageList: {
        chatId: message.chatId,
        threadId: 0,
        type: 'thread',
      },
      text: message.text,
      ...(message.entities ? { entities: message.entities } : {}),
      ...(processedAttachments ? { attachments: processedAttachments } : {}),
    });
    deleteScheduledMessage(message.id);
  }
  
  function dataURLToBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  function initializeScheduledMessages() {
    const messages = getAllScheduledMessages();
    messages.forEach(scheduleMessageTimeout);
  }

  initializeScheduledMessages();

  onScheduledMessageAdded((message) => {
    scheduleMessageTimeout(message);
  });

  onScheduledMessageRemoved((messageId) => {
    if (activeTimeouts.has(messageId)) {
      clearTimeout(activeTimeouts.get(messageId));
      activeTimeouts.delete(messageId);
    }
  });

  onBeforeUnload(() => {
    activeTimeouts.forEach(timeout => clearTimeout(timeout));
    activeTimeouts.clear();
  });
}

onBeforeUnload(() => {
  const actions = getActions();
  actions.leaveGroupCall?.({ isPageUnload: true });
  actions.hangUp?.({ isPageUnload: true });
});
